/** Sample a cover image and return two catalogue tint colours (accent + soft panel). */

export type DesignImagePalette = {
  from: string;
  to: string;
};

function clampByte(n: number): number {
  return Math.max(0, Math.min(255, Math.round(n)));
}

function rgbToHex(r: number, g: number, b: number): string {
  return `#${[r, g, b]
    .map((v) => clampByte(v).toString(16).padStart(2, '0'))
    .join('')}`;
}

function relativeLuma(r: number, g: number, b: number): number {
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

function saturation(r: number, g: number, b: number): number {
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  if (max === 0) return 0;
  return (max - min) / max;
}

function parseHexColor(input: string): { r: number; g: number; b: number } | null {
  const raw = input.trim().replace(/^#/, '');
  if (/^[0-9a-fA-F]{3}$/.test(raw)) {
    return {
      r: parseInt(raw[0]! + raw[0]!, 16),
      g: parseInt(raw[1]! + raw[1]!, 16),
      b: parseInt(raw[2]! + raw[2]!, 16),
    };
  }
  if (/^[0-9a-fA-F]{6}$/.test(raw)) {
    return {
      r: parseInt(raw.slice(0, 2), 16),
      g: parseInt(raw.slice(2, 4), 16),
      b: parseInt(raw.slice(4, 6), 16),
    };
  }
  return null;
}

/** Border / line colour that stays visible on light pages even if accent is white. */
export function designCatalogueLineColor(accent: string, panel = '#ffffff'): string {
  const a = parseHexColor(accent);
  const p = parseHexColor(panel);
  const accentLum = a ? relativeLuma(a.r, a.g, a.b) : 1;
  const panelLum = p ? relativeLuma(p.r, p.g, p.b) : 1;

  // Accent is dark enough — use it (slightly softened toward slate).
  if (a && accentLum <= 0.72) {
    return rgbToHex(
      Math.round(a.r * 0.82 + 0x64 * 0.18),
      Math.round(a.g * 0.82 + 0x74 * 0.18),
      Math.round(a.b * 0.82 + 0x8b * 0.18),
    );
  }

  // Accent is light/white: prefer a darkened panel, else slate.
  if (p && panelLum <= 0.78) {
    return rgbToHex(
      Math.round(p.r * 0.45),
      Math.round(p.g * 0.45),
      Math.round(p.b * 0.45),
    );
  }

  return '#475569';
}

/** Text / badge accent that stays readable on white (falls back if From is too light). */
export function designCatalogueAccentColor(accent: string, panel = '#ffffff'): string {
  const a = parseHexColor(accent);
  if (a && relativeLuma(a.r, a.g, a.b) <= 0.72) {
    return rgbToHex(a.r, a.g, a.b);
  }
  const line = designCatalogueLineColor(accent, panel);
  return line;
}

type Bucket = { r: number; g: number; b: number; count: number; score: number };

function quantize(v: number): number {
  // 32 levels — enough to group without washing out brand colours
  return Math.round(v / 8) * 8;
}

/**
 * Extract accent (from) + softer companion (to) from an image URL or blob URL.
 * Skips near-white / near-black / grey pixels. Falls back to defaults on failure.
 */
export async function extractDesignCoverPalette(
  imageUrl: string,
  fallback: DesignImagePalette = { from: '#9a3412', to: '#fbbf24' },
): Promise<DesignImagePalette> {
  if (!imageUrl.trim()) return fallback;

  try {
    const img = await loadImage(imageUrl);
    const canvas = document.createElement('canvas');
    const size = 64;
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return fallback;
    ctx.drawImage(img, 0, 0, size, size);
    const { data } = ctx.getImageData(0, 0, size, size);

    const buckets = new Map<string, Bucket>();
    for (let i = 0; i < data.length; i += 4) {
      const a = data[i + 3] ?? 0;
      if (a < 200) continue;
      const r = data[i] ?? 0;
      const g = data[i + 1] ?? 0;
      const b = data[i + 2] ?? 0;
      const lum = relativeLuma(r, g, b);
      const sat = saturation(r, g, b);
      // Skip washed / near-black / low-chroma pixels
      if (lum > 0.92 || lum < 0.08 || sat < 0.12) continue;

      const qr = quantize(r);
      const qg = quantize(g);
      const qb = quantize(b);
      const key = `${qr},${qg},${qb}`;
      const existing = buckets.get(key);
      // Prefer saturated mid-tones for accents
      const score = sat * 1.4 + (1 - Math.abs(lum - 0.42)) * 0.6;
      if (existing) {
        existing.count += 1;
        existing.score += score;
      } else {
        buckets.set(key, { r: qr, g: qg, b: qb, count: 1, score });
      }
    }

    const ranked = [...buckets.values()]
      .map((b) => ({ ...b, rank: b.score * Math.log2(b.count + 1) }))
      .sort((a, b) => b.rank - a.rank);

    if (ranked.length === 0) return fallback;

    const primary = ranked[0]!;
    const from = rgbToHex(primary.r, primary.g, primary.b);

    // Secondary: prefer a lighter / different hue companion, else lighten primary
    let to: string | null = null;
    for (const cand of ranked.slice(1, 12)) {
      const dist =
        Math.abs(cand.r - primary.r) + Math.abs(cand.g - primary.g) + Math.abs(cand.b - primary.b);
      if (dist < 48) continue;
      const candLum = relativeLuma(cand.r, cand.g, cand.b);
      if (candLum > relativeLuma(primary.r, primary.g, primary.b) + 0.08) {
        to = rgbToHex(cand.r, cand.g, cand.b);
        break;
      }
      if (!to && dist > 80) {
        to = rgbToHex(cand.r, cand.g, cand.b);
      }
    }

    if (!to) {
      to = rgbToHex(
        Math.min(255, primary.r + 72),
        Math.min(255, primary.g + 56),
        Math.min(255, primary.b + 28),
      );
    }

    return { from, to };
  } catch {
    return fallback;
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    // Blob / data URLs don't need CORS; remote signed URLs may.
    if (!url.startsWith('blob:') && !url.startsWith('data:')) {
      img.crossOrigin = 'anonymous';
    }
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Failed to load image for palette extraction.'));
    img.src = url;
  });
}
